import type React from 'react';
import { forwardRef, useCallback } from 'react';

import classNames from 'classnames';

import { Chip } from '@iconik-io/uad';

import Popover from '../../../ui/popover/popover';

import styles from './context_popover.module.css';

export const SEARCH_CONTEXT_GLOBAL = 'GLOBAL';
// The value is left as 'CURRENT_ENTITY' so nothing persisted or compared
// against it shifts.
/** The entity the route is showing: a collection, a saved search, a portal. */
export const SEARCH_CONTEXT_ENTITY = 'CURRENT_ENTITY';
/** The scope the route declares. @see SearchScope */
export const SEARCH_CONTEXT_SCOPE = 'ROUTE_SCOPE';

export type SearchContextType =
  | typeof SEARCH_CONTEXT_GLOBAL
  | typeof SEARCH_CONTEXT_ENTITY
  | typeof SEARCH_CONTEXT_SCOPE;

const isSearchContextType = (value: string): value is SearchContextType =>
  value === SEARCH_CONTEXT_GLOBAL ||
  value === SEARCH_CONTEXT_ENTITY ||
  value === SEARCH_CONTEXT_SCOPE;

export type SearchContextOption = {
  id: SearchContextType;
  /** The whole chip text, `In ` prefix included; long titles are elided. */
  chipLabel: string;
  /** The row's accessible name, built from the untruncated title. */
  ariaLabel: string;
  /**
   * `live` filters on every keystroke, `deferred` waits for the user to commit
   * with Enter, the search button or a row pick.
   */
  mode: SearchContextMode;
};

export type SearchContextMode = 'live' | 'deferred';

type Props = {
  /** Narrowest first, global last. @see resolveSearchContextOptions */
  options: SearchContextOption[];
  selectedContext: SearchContextType;
  onContextItemClick: (context: SearchContextType) => void;
  onContextChange: (context: SearchContextType) => void;
  show: boolean;
  query: string;
  isFocused: boolean;
};

const ContextPopover = forwardRef<HTMLDivElement, Props>(
  (
    {
      options,
      selectedContext,
      onContextItemClick,
      onContextChange,
      show,
      query,
      isFocused,
    },
    ref,
  ) => {
    const handleContextItemMouseEnter = useCallback(
      (event: React.MouseEvent<HTMLDivElement>): void => {
        const context = event.currentTarget.dataset.context;
        if (context && isSearchContextType(context)) {
          onContextChange(context);
        }
      },
      [onContextChange],
    );

    const handleContextItemClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>): void => {
        const context = event.currentTarget.dataset.context;
        if (context && isSearchContextType(context)) {
          onContextItemClick(context);
        }
      },
      [onContextItemClick],
    );

    const showPopover = show && query.length > 0;

    return (
      <Popover
        opened={showPopover}
        usePortal={false}
        vDirection="bottom"
        hDirection="right"
        className={styles.context_popup}
        animation="expand_vertical"
        isFullWidth
      >
        <div
          className={classNames(
            styles.context_popup_content,
            isFocused ? styles.context_popup_content_focused : undefined,
          )}
          ref={ref}
        >
          {options.map((option) => {
            const isSelected = option.id === selectedContext;
            return (
              <div
                key={option.id}
                className={classNames(styles.context_item, {
                  [styles.selected]: isSelected,
                })}
                data-context={option.id}
                onMouseEnter={handleContextItemMouseEnter}
                onClick={handleContextItemClick}
                role="button"
                aria-label={option.ariaLabel}
                tabIndex={0}
              >
                <div className={styles.context_item_query}>{query}</div>
                <Chip
                  size="s"
                  color={isSelected ? 'inverted' : 'default'}
                  truncate
                >
                  {option.chipLabel}
                </Chip>
              </div>
            );
          })}
        </div>
      </Popover>
    );
  },
);

export default ContextPopover;
